import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Cookie, ChevronDown, Mail, MapPin, Info } from 'lucide-react';

const SECTIONS = [
  {
    id: 'what',
    title: '1. What Are Cookies?',
    content: `Cookies are small text files placed on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work efficiently, improve user experience, and provide information to website owners.

Cookies can be:
• **Session cookies** — temporary, deleted when you close your browser
• **Persistent cookies** — remain on your device for a set period or until manually deleted
• **First-party cookies** — set by the website you are visiting (vibhraad.com)
• **Third-party cookies** — set by external services embedded on the page (e.g., Google Analytics)

We use cookies to ensure our website functions correctly and to understand how visitors use it so we can continually improve.`,
  },
  {
    id: 'types',
    title: '2. Cookies We Use',
    content: `**Essential Cookies (Always Active)**
These cookies are strictly necessary for the website to function. They cannot be disabled.

| Cookie | Purpose | Duration |
|--------|---------|----------|
| Session | Maintains your session state | Session |
| CSRF Token | Protects form submissions from cross-site forgery | Session |
| Cookie Consent | Stores your cookie preferences | 12 months |

**Analytics Cookies (Google Analytics)**
These help us understand website traffic and user behaviour. All data is anonymised and aggregated.

| Cookie | Purpose | Duration |
|--------|---------|----------|
| _ga | Distinguishes users | 2 years |
| _ga_[ID] | Maintains session state | 2 years |
| _gid | Distinguishes users | 24 hours |
| _gat | Throttles request rate | 1 minute |

**Preference Cookies**
These remember your settings and choices to improve your experience on return visits.

| Cookie | Purpose | Duration |
|--------|---------|----------|
| Theme preference | Remembers display settings | 6 months |
| Language | Remembers language selection | 12 months |`,
  },
  {
    id: 'analytics',
    title: '3. Google Analytics',
    content: `We use Google Analytics 4 (GA4) to collect anonymised data about how visitors use our website. This helps us improve content, navigation, and performance.

**What Google Analytics collects:**
• Pages visited and time spent on each page
• Referring website (where you came from)
• Browser type, device type, and operating system
• Geographic location (country/city level only)
• User interactions (button clicks, form starts, scroll depth)

**What it does NOT collect:**
• Your name, email, or personally identifiable information
• Payment or financial data
• Specific addresses or contact details

**Data Processing:** Analytics data is processed by Google LLC, subject to their Privacy Policy and the EU-US Data Privacy Framework. You can opt out of Google Analytics tracking across all websites using the [Google Analytics Opt-out Browser Add-on](https://tools.google.com/dlpage/gaoptout).

**IP Anonymisation:** We have enabled IP anonymisation in GA4. Your full IP address is never stored.`,
  },
  {
    id: 'third-party',
    title: '4. Third-Party Services',
    content: `Our website uses the following third-party services that may set cookies or use similar technologies:

**Formspree**
Used to process contact form submissions. When you submit a form, Formspree processes the data on their secure servers. They may use session cookies to prevent spam submissions.

**Google Fonts**
We use Google Fonts to render typography. Google may collect data related to font delivery. No personally identifiable data is shared.

**Lucide Icons / React Libraries**
These are loaded as part of our application bundle and do not set cookies.

**Social Media Embeds**
If we embed social media content (LinkedIn, Facebook), those platforms may set their own cookies. We do not control these third-party cookies. Refer to each platform's cookie policy for details.`,
  },
  {
    id: 'manage',
    title: '5. Managing Your Cookie Preferences',
    content: `**Browser Settings**
You can control and manage cookies through your browser settings. Most browsers allow you to:
• View and delete existing cookies
• Block all or specific cookies
• Set preferences for specific websites
• Enable "Private Browsing" mode which limits cookie storage

**How to manage cookies in your browser:**
• **Chrome:** Settings → Privacy & Security → Cookies and other site data
• **Firefox:** Settings → Privacy & Security → Cookies and Site Data
• **Safari:** Preferences → Privacy → Manage Website Data
• **Edge:** Settings → Cookies and site permissions

**Impact of Disabling Cookies**
Disabling essential cookies may affect website functionality. The contact form may not work correctly, and your preferences may not be saved between visits.

Disabling analytics cookies will prevent us from collecting usage data but will not affect your ability to use the website.`,
  },
  {
    id: 'updates',
    title: '6. Updates to This Policy',
    content: `We review and update this Cookie Policy periodically to reflect changes in technology, law, or our practices.

• Changes will be posted on this page with an updated "Last Updated" date.
• For significant changes affecting how we use your data, we will notify you via a website banner.
• Continued use of our website after changes are posted constitutes acceptance of the updated policy.

If you have questions about specific cookies or need to report a concern, contact us at vibhraad@gmail.com.`,
  },
];

function AccordionItem({ section, index }) {
  const [open, setOpen] = useState(index === 0);

  const formatContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.trim() === '') return <br key={i} />;
      // Handle table rows
      if (line.trim().startsWith('|')) {
        return <p key={i} className="mb-1 text-gray-600 text-sm font-mono leading-relaxed">{line}</p>;
      }
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="mb-2 text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.04 }}
      className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm mb-3"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-bold text-gray-900 text-sm">{section.title}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown className="w-5 h-5 text-emerald-500 flex-shrink-0" />
        </motion.span>
      </button>
      {open && (
        <div className="px-6 pb-5 pt-2 bg-gray-50/50 border-t border-gray-100">
          {formatContent(section.content)}
        </div>
      )}
    </motion.div>
  );
}

export default function CookiePolicy() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gray-950 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-emerald-700/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-600/30">
              <Cookie className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Cookie Policy</h1>
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed mb-4">
              We use cookies to make our website work properly and understand how you use it. Here's exactly what we use and why.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-gray-400 text-xs px-4 py-2 rounded-full">
              Last Updated: April 16, 2026 · Effective: October 2025
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 max-w-4xl mx-auto px-5">
        {/* Info box */}
        <div className="flex gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl p-5 mb-8">
          <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-emerald-800 text-sm leading-relaxed">
            <strong>Summary:</strong> We use essential cookies to run the website, analytics cookies (Google Analytics, anonymised) to understand traffic, and preference cookies to remember your settings. You can manage or disable non-essential cookies through your browser settings.
          </p>
        </div>

        {SECTIONS.map((section, i) => (
          <AccordionItem key={section.id} section={section} index={i} />
        ))}

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 bg-gray-950 rounded-3xl p-8 text-white"
        >
          <h2 className="text-xl font-bold mb-2">Questions About Cookies?</h2>
          <p className="text-gray-400 text-sm mb-5">Contact us if you have questions about specific cookies or want to request data deletion.</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-emerald-400" />
              </div>
              <a href="mailto:vibhraad@gmail.com" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors">vibhraad@gmail.com</a>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="text-gray-400 text-sm">Vibhraad Technology Pvt Ltd, F-375, Sector-36, Noida, UP 201301</span>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
